//----- Component --------------------------------------------------------/
// Developer : Basado en FileUploadHS
// Creation Date : 2024
// Version : 1
// Description : Componente para visualizar y gestionar archivos adjuntos de envasado
//------Changes ----------------------------------------------------------/
import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  SnackbarContent,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Box,
} from "@mui/material";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { UrlApi } from "../../../services/apirest";


// Función para descargar archivos (puedes ajustarla según tu implementación)
const DownloadFiles = async (filename) => {
  try {
    const response = await fetch(UrlApi + "/uploadfiles/download/" + filename);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.split('-').slice(1).join('-'); // Quita el ID del nombre
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error descargando archivo:", error);
  }
};

function FileUploadEnvasado({ 
  open,           // Controla si el diálogo está abierto
  fileId,         // ID único para asociar los archivos (ej: "payment-123")
  onClose,        // Función para cerrar el diálogo
  canEdit = false, // Si puede editar (subir/borrar archivos)
  title = "Archivos Adjuntos" // Título personalizable
}) {
  const [openclose, setOpenClose] = useState(open);
  const [fileList, setFileList] = useState([]);
  const [snack, setSnack] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [colortexto, setColorTexto] = useState("white");
  const [colorfondo, setColorFondo] = useState("black");
  const [archivos, setArchivos] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);

  // Sincronizar el estado local con la prop open
  useEffect(() => {
    setOpenClose(open);
  }, [open]);

  // Cargar archivos cuando se abre el diálogo o cambia fileId
  useEffect(() => {
    if (open && fileId) {
      cargarArchivos();
    }
  }, [open, fileId]);

  // Recargar cuando se cierra el snack (después de borrar/subir)
  useEffect(() => {
    if (!snack && open && fileId) {
      cargarArchivos();
    }
  }, [snack]);

  const cargarArchivos = async () => {
    try {
      setLoading(true);
      const response = await fetch(UrlApi + "/uploadfiles/todownload/" + fileId);
      const data = await response.json();
      setArchivos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando archivos:", error);
      setArchivos([]);
      setMensaje("Error al cargar los archivos");
      setColorFondo("red");
      setSnack(true);
    } finally {
      setLoading(false);
    }
  };

  const borraAdjunto = async (e, id, archive) => {
    e.preventDefault();
    if (window.confirm("¿Seguro de borrar el archivo " + archive + "?")) {
      try {
        const response = await fetch(UrlApi + "/uploadfiles/todownload/" + id, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        });
        
        if (response.ok) {
          setMensaje("Archivo borrado correctamente");
          setColorFondo("green");
          setSnack(true);
        } else {
          setMensaje("No se pudo borrar el archivo");
          setColorFondo("red");
          setSnack(true);
        }
      } catch (error) {
        console.error("Error borrando archivo:", error);
        setMensaje("Error al borrar el archivo");
        setColorFondo("red");
        setSnack(true);
      }
    }
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") return;
    setSnack(false);
  };

  const handleFileChange = (e) => {
    setFileList(e.target.files);
  };

  const removefiles = (index) => {
    const s = Array.from(fileList).filter((item, i) => i !== index);
    setFileList(s);
  };

  const handleClose = () => {
    setOpenClose(false);
    setFileList([]);
    setImageUrl("");
    if (onClose) {
      onClose();
    }
  };

  const handleUploadClick = async () => {
    if (!fileList || fileList.length === 0) {
      setMensaje("No hay archivos para guardar");
      setColorFondo("red");
      setSnack(true);
      return;
    }

    const data = new FormData();
    data.append("uid", fileId);
    
    for (let i = 0; i < fileList.length; i++) {
      data.append("archivo", fileList[i]);
    }

    try {
      setLoading(true);
      const response = await fetch(UrlApi + "/uploadfiles/uploadfiles", {
        method: "POST",
        body: data,
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setMensaje(result.message || "Archivos guardados correctamente");
        setColorFondo("green");
        setFileList([]);
      } else {
        setMensaje(result.message || "Error al guardar los archivos");
        setColorFondo("red");
      }
      setSnack(true);
    } catch (error) {
      console.error("Error subiendo archivos:", error);
      setMensaje("Error al subir los archivos");
      setColorFondo("red");
      setSnack(true);
    } finally {
      setLoading(false);
    }
  };

  const verArchivo = async (archivo, extension) => {
    try {
      const fileName = archivo.id_archivo + "-" + archivo.nombre;
      const response = await fetch(UrlApi + "/uploadfiles/uploads/" + fileName);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Abrir en nueva ventana
      window.open(url, "_blank");
      
      // Limpiar la URL después de un tiempo
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Error abriendo archivo:", error);
      setMensaje("Error al abrir el archivo");
      setColorFondo("red");
      setSnack(true);
    }
  };

  const getFileIcon = (extension) => {
    if (extension.includes("pdf")) return "📄";
    if (extension.includes("image")) return "🖼️";
    if (extension.includes("word")) return "📝";
    if (extension.includes("excel")) return "📊";
    return "📎";
  };

  return (
    <>
      <Dialog
        open={openclose}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: "auto",
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: "#0979B0", 
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}>
          <AttachFileIcon />
          {title}
        </DialogTitle>

        <DialogContent dividers>
          {/* Área de subida de archivos (solo si canEdit es true) */}
          {canEdit && (
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<ContentPasteSearchIcon />}
                sx={{ mb: 2 }}
              >
                Agregar archivos
                <input
                  hidden
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                />
              </Button>

              {/* Lista de archivos a subir */}
              {fileList.length > 0 && (
                <Table size="small">
                  <TableBody>
                    {Array.from(fileList).map((file, i) => (
                      <TableRow key={i}>
                        <TableCell padding="none" width={50}>
                          <IconButton size="small" onClick={() => removefiles(i)}>
                            <DeleteIcon color="error" fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <span>{getFileIcon(file.type)}</span>
                            <Typography variant="body2">
                              {file.name} ({(file.size / 1024).toFixed(2)} KB)
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Lista de archivos existentes */}
          <Typography variant="subtitle2" gutterBottom>
            Archivos adjuntos:
          </Typography>

          {loading ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
              Cargando archivos...
            </Typography>
          ) : archivos.length > 0 ? (
            <Table size="small">
              <TableBody>
                {archivos.map((archivo) => (
                  <TableRow key={archivo.id}>
                    <TableCell padding="none" width={120}>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => verArchivo(archivo, archivo.extension)}
                          title="Ver archivo"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {/* <IconButton
                          size="small"
                          color="success"
                          onClick={() => DownloadFiles(archivo.id_archivo + "-" + archivo.nombre)}
                          title="Descargar archivo"
                        >
                          <DownloadForOfflineIcon fontSize="small" />
                        </IconButton> */}
                        {canEdit && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => borraAdjunto(e, archivo.id, archivo.nombre)}
                            title="Eliminar archivo"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <span>{getFileIcon(archivo.extension)}</span>
                        <Typography variant="body2">
                          {archivo.nombre}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              align="center" 
              sx={{ py: 3, fontStyle: "italic" }}
            >
              No hay archivos adjuntos
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined" color="error">
            Cerrar
          </Button>
          {canEdit && fileList.length > 0 && (
            <Button
              onClick={handleUploadClick}
              variant="contained"
              color="success"
              disabled={loading}
            >
              Guardar Archivos
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snack}
        autoHideDuration={3000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <SnackbarContent
          style={{
            backgroundColor: colorfondo,
            color: colortexto,
            fontWeight: "bold",
          }}
          message={mensaje}
        />
      </Snackbar>
    </>
  );
}

export default FileUploadEnvasado;